// /join/:token — a caretaker redeems an invite link. Public route (no auth
// guard). Primary path is Google OAuth so the caretaker has a portable identity
// that survives PWA installs and device swaps; the anonymous-session path is
// kept as a backup for browser-only quick joins.

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { supabase } from "../lib/supabase";
import { lastUsedBabyId } from "../lib/babyId";
import { useT } from "../lib/LocaleContext";

export function Join() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { session, signInWithGoogle } = useAuth();
  const t = useT();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAnon, setShowAnon] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const joinedRef = useRef(false);

  // Auto-claim the invite as soon as we have a non-anonymous session — that
  // covers (a) returning from Google OAuth and (b) a creator already signed in
  // who opens an invite link. Anonymous joins still require an explicit submit
  // because the user picks their own display_name first.
  useEffect(() => {
    if (!session || joinedRef.current) return;
    if (session.user.is_anonymous) return;
    joinedRef.current = true;
    const meta = (session.user.user_metadata ?? {}) as Record<string, unknown>;
    const profileName =
      typeof meta.full_name === "string" ? meta.full_name :
      typeof meta.name === "string" ? meta.name : "";
    void redeemInvite(profileName.trim());
  }, [session]);

  async function redeemInvite(displayName: string) {
    setBusy(true);
    setError(null);
    try {
      const { data: s } = await supabase.auth.getSession();
      const res = await fetch("/api/join", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${s.session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ token, displayName }),
      });
      const data = (await res.json()) as { ok: boolean; babyId?: string; reason?: string };
      if (!data.ok || !data.babyId) {
        joinedRef.current = false;
        setBusy(false);
        setError(
          data.reason === "expired" ? t.join.expired :
          data.reason === "invalid-token" ? t.join.invalid :
          t.join.joinFailed,
        );
        return;
      }
      lastUsedBabyId.set(s.session?.user.id, data.babyId);
      navigate(`/b/${data.babyId}`, { replace: true });
    } catch {
      joinedRef.current = false;
      setBusy(false);
      setError(t.common.somethingWrong);
    }
  }

  async function handleGoogle() {
    setBusy(true);
    setError(null);
    const { error: err } = await signInWithGoogle(
      `${window.location.origin}/babyhelper/join/${token}`,
    );
    if (err) {
      setError(err);
      setBusy(false);
    }
    // On success the browser redirects to Google.
  }

  async function handleAnonSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: anonErr } = await supabase.auth.signInAnonymously();
    if (anonErr) {
      setError(t.join.sessionError);
      setBusy(false);
      return;
    }
    await redeemInvite(name.trim());
  }

  // Auto-join in flight — we're either returning from OAuth or signed-in already.
  if (session && !session.user.is_anonymous) {
    return (
      <main className="screen screen--center">
        <header className="hero">
          <div className="hero__emoji" aria-hidden="true">✨</div>
          <h1>{t.join.title}</h1>
          <p className="hero__sub">{t.join.submitting}</p>
        </header>
        {error && <p className="error" role="alert">{error}</p>}
      </main>
    );
  }

  return (
    <main className="screen screen--center">
      <header className="hero">
        <div className="hero__emoji" aria-hidden="true">👶</div>
        <h1>{t.join.title}</h1>
        <p className="hero__sub">{t.join.subtitle}</p>
      </header>

      <button
        className="btn btn--primary btn--block"
        type="button"
        disabled={busy}
        onClick={() => void handleGoogle()}
      >
        {busy ? t.join.googleSubmitting : t.join.google}
      </button>
      <p className="join__google-hint">{t.join.googleHint}</p>

      {!showAnon ? (
        <button
          className="btn btn--ghost btn--block"
          type="button"
          onClick={() => setShowAnon(true)}
          disabled={busy}
        >
          {t.join.anonAlt}
        </button>
      ) : (
        <form className="form" onSubmit={handleAnonSubmit}>
          <label className="field">
            <span className="field__label">{t.join.nameLabel}</span>
            <input
              className="field__input"
              type="text"
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.join.namePlaceholder}
              autoCapitalize="words"
            />
            <span className="field__hint">{t.join.anonHint}</span>
          </label>
          <button
            className="btn btn--ghost btn--block"
            type="submit"
            disabled={busy || !name.trim()}
          >
            {busy ? t.join.submitting : t.join.submit}
          </button>
        </form>
      )}

      {error && <p className="error" role="alert">{error}</p>}
    </main>
  );
}
